import { useCallback, useMemo, useRef } from 'react';
import { clearCameraVideoStreamCache } from 'services/devices/cameras/camera.service';
import { PLAYBACK_STATUS, RECOVERY_GRACE_MS, RETRY_BACKOFF_MS } from '../config/videoPlayer.config';
import {
  getAvailableVideoTimeRange,
  getCameraIdFromVideoStreamUrl,
} from '../utils/videoPlayer.utils';

/**
 * Handles attempts to keep a video stream going after playback stalls, ends, or fails.
 *
 * The hook first waits to see whether already-loaded video can continue playing. If it
 * cannot, the hook schedules a stream restart with a delay, sometimes clears the camera
 * stream cache, and avoids restarting when playback is paused, idle, or blocked.
 */
const useVideoStreamRecovery = ({
  isStillCurrentPlaybackRequest, // Function to check if the current playback request is still valid
  pauseWatchTimeLimitTimer, // Pauses the watchtime limit timer
  playbackStatusRef, // Ref to the current playback status (latest playback status, even if the component has re-rendered)
  playAfterDelay, // Function to attempt to play the video after a short delay
  retryAttemptRef, // Ref to the current retry attempt number
  resetVideoPlayer, // Function that resets the current broken video setup before attempting to restart the video stream
  getIsWatchTimeLimitReached, // Function that returns whether playback should stay blocked by the watch time limit
  setPlaybackStatus, // Setter to update the playback status (both ref and state)
  skipToNewestReadyVideo, // Function to move the video forward to the newest playable point.
  src, // Contains the camera video stream URL (used to get camera ID for clearing the video stream cache)
  startNewPlaybackRequest, // Function that creates a new playback request ID and sets the playback status.
  startWatchTimeLimitTimer, // Starts the watchtime limit timer
  tryToPlayVideo, // Function to attempt to play the video
  updateReadyVideoProgress, // Function to update the ready video progress
  videoRef, // Ref to the HTML video element
}) => {
  const pendingStallRecoveryRef = useRef(null);
  const pendingStreamRestartRef = useRef(null);

  const cancelPendingRecovery = useCallback(() => {
    [pendingStallRecoveryRef, pendingStreamRestartRef].forEach((pendingDelayRef) => {
      if (pendingDelayRef.current) clearTimeout(pendingDelayRef.current);
      pendingDelayRef.current = null;
    });
  }, []);

  // Hard recovery which tears down the current video setup and creates a new one. Used when cannot be recovered by skipping to the newest ready video
  const restartStreamAfterPlaybackProblem = useCallback(
    async ({ allowRestartWhilePaused = false, forceStreamCacheClear = false } = {}) => {
      cancelPendingRecovery();

      if (
        getIsWatchTimeLimitReached() ||
        (!allowRestartWhilePaused && playbackStatusRef.current === PLAYBACK_STATUS.paused) ||
        playbackStatusRef.current === PLAYBACK_STATUS.idle
      ) {
        return;
      }

      const requestId = startNewPlaybackRequest(PLAYBACK_STATUS.recovering);
      retryAttemptRef.current += 1;

      const retryAttempt = retryAttemptRef.current;
      const backoffMs = RETRY_BACKOFF_MS[Math.min(retryAttempt - 1, RETRY_BACKOFF_MS.length - 1)];
      const shouldClearCache = forceStreamCacheClear || retryAttempt % 3 === 0;

      resetVideoPlayer({ nextStatus: PLAYBACK_STATUS.recovering, requestId });

      pendingStreamRestartRef.current = setTimeout(async () => {
        pendingStreamRestartRef.current = null;

        const canRestartStream = () =>
          isStillCurrentPlaybackRequest(requestId) &&
          !getIsWatchTimeLimitReached() &&
          playbackStatusRef.current !== PLAYBACK_STATUS.paused &&
          playbackStatusRef.current !== PLAYBACK_STATUS.idle;

        if (!canRestartStream()) return;

        if (shouldClearCache) {
          const cameraId = getCameraIdFromVideoStreamUrl(src);
          if (cameraId) await clearCameraVideoStreamCache(cameraId);
        }

        // Stop, pause, source changes, or unmount can invalidate this request while
        // cache clearing is in flight, even after its timeout has already fired.
        if (!canRestartStream()) return;

        playAfterDelay(Math.min(retryAttempt * 600, 2400)); // Attempts to play video after either retry attempt times 600ms or 2400ms, whichever is less
      }, backoffMs);
    },
    [
      cancelPendingRecovery,
      getIsWatchTimeLimitReached,
      isStillCurrentPlaybackRequest,
      playAfterDelay,
      playbackStatusRef,
      resetVideoPlayer,
      retryAttemptRef,
      src,
      startNewPlaybackRequest,
    ],
  );

  // Soft recovery which attempts to skip to the newest ready video and play it. If that fails, it will fall back to a hard recovery.
  const recoverFromPlaybackStall = useCallback(
    (reason = 'unknown', options = {}) => {
      if (getIsWatchTimeLimitReached() || playbackStatusRef.current !== PLAYBACK_STATUS.playing)
        return;

      const requestId = startNewPlaybackRequest(PLAYBACK_STATUS.recovering);
      cancelPendingRecovery();
      pauseWatchTimeLimitTimer();

      const video = videoRef.current;
      if (video) video.pause();

      // Waiting briefly gives the stream a chance to load new video before we try to skip forward.
      pendingStallRecoveryRef.current = setTimeout(() => {
        pendingStallRecoveryRef.current = null;

        const currentVideo = videoRef.current;
        const readyVideoRange = getAvailableVideoTimeRange(currentVideo);
        // Measure video AHEAD of the playhead, not the total buffered window. The
        // back-buffer (backBufferLength) keeps ~30s of already-played video behind us,
        // so availableVideoDuration stays large even on a dead stream and would keep us
        // looping in soft recovery forever. If nothing new is ahead, fall through to a
        // hard restart (which clears the camera cache and reloads the manifest).
        const hasReadyVideoAhead =
          currentVideo &&
          readyVideoRange &&
          readyVideoRange.latestAvailableTimestamp - currentVideo.currentTime > 0.5;

        if (currentVideo && hasReadyVideoAhead) {
          skipToNewestReadyVideo();

          tryToPlayVideo(currentVideo, requestId, `Recovered playback (${reason})`).then(
            (playResult) => {
              if (!isStillCurrentPlaybackRequest(requestId) || getIsWatchTimeLimitReached()) return;

              if (!playResult.didPlay || currentVideo.paused || currentVideo.ended) {
                restartStreamAfterPlaybackProblem(options);
                return;
              }

              setPlaybackStatus(PLAYBACK_STATUS.playing);
              updateReadyVideoProgress();
              startWatchTimeLimitTimer();
            },
          );

          return;
        }

        restartStreamAfterPlaybackProblem(options);
      }, RECOVERY_GRACE_MS);
    },
    [
      cancelPendingRecovery,
      getIsWatchTimeLimitReached,
      isStillCurrentPlaybackRequest,
      pauseWatchTimeLimitTimer,
      playbackStatusRef,
      restartStreamAfterPlaybackProblem,
      setPlaybackStatus,
      skipToNewestReadyVideo,
      startNewPlaybackRequest,
      startWatchTimeLimitTimer,
      tryToPlayVideo,
      updateReadyVideoProgress,
      videoRef,
    ],
  );

  return useMemo(
    () => ({
      cancelPendingRecovery,
      restartStreamAfterPlaybackProblem,
      recoverFromPlaybackStall,
    }),
    [cancelPendingRecovery, restartStreamAfterPlaybackProblem, recoverFromPlaybackStall],
  );
};

export default useVideoStreamRecovery;
